import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { api, formatMoney } from '../api';

/** Shared pieces of the Today, Customers and customer desk screens. Money arrives as exact minor-unit strings. */
export interface Money { minor: string; currency: string }
export const bucketLabels: Record<string, string> = {
  current: 'Not yet due', '1-30': '1–30 days', '31-60': '31–60 days', '61-90': '61–90 days', '90+': 'Over 90 days',
};

export function useDeskData<T>(path: string) {
  const [data, setData] = useState<T | null>(null); const [error, setError] = useState('');
  const [round, setRound] = useState(0);
  const reload = useCallback(() => setRound(r => r + 1), []);
  useEffect(() => {
    let active = true;
    const load = () => { void api<T>(path).then(v => { if (active) { setData(v); setError(''); } })
      .catch((e: unknown) => { if (active) setError(e instanceof Error ? e.message : 'This page could not load.'); }); };
    load(); window.addEventListener('alder:state-changed', load);
    return () => { active = false; window.removeEventListener('alder:state-changed', load); };
  }, [path, round]);
  return { data, error, reload };
}

const dateFormat = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
const yearFormat = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
/** A calendar date (YYYY-MM-DD) shown without a time-zone shift; the year appears only when it differs from `asOf`. */
export function formatDay(date: string, asOf?: string) {
  const value = new Date(`${date.slice(0, 10)}T00:00:00Z`);
  return (asOf && asOf.slice(0, 4) !== date.slice(0, 4) ? yearFormat : dateFormat).format(value);
}
export function formatLongDay(date: string) {
  return new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(`${date}T00:00:00Z`));
}
export function formatMoment(instant: string, asOf?: string) {
  if (instant.length === 10) return formatDay(instant, asOf);
  const value = new Date(instant);
  const day = formatDay(value.toISOString().slice(0, 10), asOf);
  return `${day}, ${new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(value)}`;
}
export function daysBetween(from: string, to: string) {
  return Math.round((Date.parse(`${to.slice(0, 10)}T00:00:00Z`) - Date.parse(`${from.slice(0, 10)}T00:00:00Z`)) / 86_400_000);
}
export function ago(at: string, asOf: string) {
  const days = daysBetween(at, asOf);
  return days <= 0 ? 'Today' : days === 1 ? 'Yesterday' : `${days} days ago`;
}

export function MoneyLines({ values, empty = '–' }: { values: Money[]; empty?: string }) {
  if (!values.length) return <span className="muted">{empty}</span>;
  return <span className="money-lines">{values.map(v => <span key={v.currency}>{formatMoney(v.minor, v.currency)}</span>)}</span>;
}

export interface Badges { paused: boolean; promise: boolean; brokenPromise: boolean; dispute: boolean }
export function StatusBadges({ badges, status }: { badges: Badges; status?: string }) {
  const list: [string, string][] = [];
  if (status && status !== 'active') list.push([status, status === 'suspended' ? 'Suspended' : 'Churned']);
  if (badges.dispute) list.push(['dispute', 'Dispute']);
  if (badges.brokenPromise) list.push(['broken', 'Broken promise']);
  if (badges.promise) list.push(['promise', 'Promise to pay']);
  if (badges.paused && !badges.dispute) list.push(['paused', 'Paused']);
  return <span className="desk-badges">
    {list.map(([kind, label]) => <span key={kind} className={`desk-badge ${kind}`}>{label}</span>)}</span>;
}

/** One currency's open AR split by age. Segment widths are exact ratios of minor units; nothing is converted. */
export function AgingBar({ currency, buckets, compact = false }:
  { currency: string; buckets: { bucket: string; minor: string; count?: number }[]; compact?: boolean }) {
  const total = buckets.reduce((s, b) => s + BigInt(b.minor), 0n);
  const share = (minor: string) => (total === 0n ? 0 : Number((BigInt(minor) * 10_000n) / total) / 100);
  return <figure className={`aging ${compact ? 'compact' : ''}`} aria-label={`${currency} aging`}>
    {!compact && <figcaption><strong>{currency}</strong><span>{formatMoney(total, currency)} open</span></figcaption>}
    <div className="aging-track" role="img"
      aria-label={buckets.map(b => `${bucketLabels[b.bucket]} ${formatMoney(b.minor, currency)}`).join(', ')}>
      {total === 0n ? <span className="aging-empty" /> : buckets.filter(b => b.minor !== '0').map(b =>
        <span key={b.bucket} className={`aging-segment b-${b.bucket.replace('+', 'plus')}`} style={{ flexGrow: share(b.minor) }}
          title={`${bucketLabels[b.bucket]}: ${formatMoney(b.minor, currency)}`} />)}
    </div>
    <dl className="aging-legend">{buckets.map(b => <div key={b.bucket} className={b.minor === '0' ? 'zero' : ''}>
      <dt><i className={`b-${b.bucket.replace('+', 'plus')}`} />{bucketLabels[b.bucket]}</dt>
      <dd>{formatMoney(b.minor, currency)}{b.count !== undefined && <small>{b.count} {b.count === 1 ? 'invoice' : 'invoices'}</small>}</dd>
    </div>)}</dl>
  </figure>;
}

export function DeskSection({ title, aside, children, className = '' }:
  { title: string; aside?: ReactNode; children: ReactNode; className?: string }) {
  return <section className={`desk-section ${className}`} aria-label={title}>
    <header><h2>{title}</h2>{aside}</header>{children}
  </section>;
}

export function EmptyNote({ icon, title, children }: { icon: ReactNode; title: string; children?: ReactNode }) {
  return <div className="desk-empty">{icon}<strong>{title}</strong>{children && <p>{children}</p>}</div>;
}
